import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { apiClient } from '@/lib/api-client';
import { queryKeys } from '@/lib/query-client';
import type {
  GearRequest,
  GearRequestLine,
  CreateGearRequestPayload,
  UpdateGearRequestPayload,
  ReceiveGearRequestPayload,
  ScanGearRequestPayload,
  DeployGearRequestPayload,
  ConfirmSupervisorRequestPayload,
  GearRequestStatus,
  PaginatedResponse,
} from '@/lib/types';

const GEAR_REQUESTS_API = '/api/v1/gear-requests';

// ─── Filter Types ───────────────────────────────────────────────────────────

export interface GearRequestFilters {
  page?: number;
  page_size?: number;
  project?: number | '';
  // Single GearRequestStatus or a comma-separated list of statuses.
  status?: GearRequestStatus | string;
  priority?: string;
  needed_by_before?: string;
  needed_by_after?: string;
  ordering?: string;
}

function cleanFilters<T extends object>(filters: T): Record<string, unknown> {
  return Object.fromEntries(
    Object.entries(filters).filter(([, value]) => value !== '' && value !== undefined)
  );
}

// ─── List ───────────────────────────────────────────────────────────────────

export function useGearRequests(filters: GearRequestFilters = {}, enabled = true) {
  const clean = cleanFilters(filters);
  return useQuery({
    queryKey: queryKeys.gearRequests.list(clean),
    queryFn: async (): Promise<PaginatedResponse<GearRequest>> => {
      const response = await apiClient.get<PaginatedResponse<GearRequest>>(
        `${GEAR_REQUESTS_API}/`,
        { params: clean }
      );
      return response.data;
    },
    enabled,
  });
}

// ─── Detail ─────────────────────────────────────────────────────────────────

export function useGearRequest(id: number) {
  return useQuery({
    queryKey: queryKeys.gearRequests.detail(id),
    queryFn: async (): Promise<GearRequest> => {
      const response = await apiClient.get<GearRequest>(
        `${GEAR_REQUESTS_API}/${id}/`
      );
      return response.data;
    },
    enabled: id > 0,
  });
}

// ─── Create (G02) ──────────────────────────────────────────────────────────

export function useCreateGearRequest() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (data: CreateGearRequestPayload): Promise<GearRequest> => {
      const response = await apiClient.post<GearRequest>(
        `${GEAR_REQUESTS_API}/`,
        data
      );
      return response.data;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: queryKeys.gearRequests.all });
      qc.invalidateQueries({ queryKey: queryKeys.crossModule.all });
    },
  });
}

// ─── Update (G02) ──────────────────────────────────────────────────────────

export function useUpdateGearRequest() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({
      id,
      data,
    }: {
      id: number;
      data: UpdateGearRequestPayload;
    }): Promise<GearRequest> => {
      const response = await apiClient.patch<GearRequest>(
        `${GEAR_REQUESTS_API}/${id}/`,
        data
      );
      return response.data;
    },
    onSuccess: (_, variables) => {
      qc.invalidateQueries({ queryKey: queryKeys.gearRequests.all });
      qc.invalidateQueries({ queryKey: queryKeys.gearRequests.detail(variables.id) });
      qc.invalidateQueries({ queryKey: queryKeys.crossModule.all });
    },
  });
}

// ─── Fulfill (G03) ─────────────────────────────────────────────────────────

export function useFulfillGearRequest() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({
      id,
      notes,
    }: {
      id: number;
      notes?: string;
    }): Promise<GearRequest> => {
      const response = await apiClient.post<GearRequest>(
        `${GEAR_REQUESTS_API}/${id}/fulfill/`,
        { notes: notes || '' }
      );
      return response.data;
    },
    onSuccess: (_, variables) => {
      qc.invalidateQueries({ queryKey: queryKeys.gearRequests.all });
      qc.invalidateQueries({
        queryKey: queryKeys.gearRequests.detail(variables.id),
      });
      qc.invalidateQueries({ queryKey: queryKeys.crossModule.all });
    },
  });
}

// ─── Mark In Transit (G03 / G06) ───────────────────────────────────────────

export function useMarkGearRequestInTransit() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({
      id,
      notes,
      line_assets,
      line_kits,
    }: {
      id: number;
      notes?: string;
      line_assets?: Record<string, number>;
      line_kits?: Record<string, number>;
    }): Promise<GearRequest> => {
      const response = await apiClient.post<GearRequest>(
        `${GEAR_REQUESTS_API}/${id}/mark-in-transit/`,
        {
          notes: notes || '',
          ...(line_assets && Object.keys(line_assets).length > 0 ? { line_assets } : {}),
          ...(line_kits && Object.keys(line_kits).length > 0 ? { line_kits } : {}),
        }
      );
      return response.data;
    },
    onSuccess: (_, variables) => {
      qc.invalidateQueries({ queryKey: queryKeys.gearRequests.all });
      qc.invalidateQueries({
        queryKey: queryKeys.gearRequests.detail(variables.id),
      });
      qc.invalidateQueries({ queryKey: queryKeys.crossModule.all });
      qc.invalidateQueries({ queryKey: queryKeys.deployments.all });
      qc.invalidateQueries({ queryKey: queryKeys.assets.all });
    },
  });
}

// ─── Cancel (G03) ──────────────────────────────────────────────────────────

export function useCancelGearRequest() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({
      id,
      notes,
    }: {
      id: number;
      notes?: string;
    }): Promise<GearRequest> => {
      const response = await apiClient.post<GearRequest>(
        `${GEAR_REQUESTS_API}/${id}/cancel/`,
        { notes: notes || '' }
      );
      return response.data;
    },
    onSuccess: (_, variables) => {
      qc.invalidateQueries({ queryKey: queryKeys.gearRequests.all });
      qc.invalidateQueries({
        queryKey: queryKeys.gearRequests.detail(variables.id),
      });
      qc.invalidateQueries({ queryKey: queryKeys.crossModule.all });
    },
  });
}

// ─── Receive (G05) ─────────────────────────────────────────────────────────

export function useReceiveGearRequest() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({
      id,
      data,
    }: {
      id: number;
      data: ReceiveGearRequestPayload;
    }): Promise<GearRequest> => {
      const response = await apiClient.post<GearRequest>(
        `${GEAR_REQUESTS_API}/${id}/receive/`,
        data
      );
      return response.data;
    },
    onSuccess: (_, variables) => {
      qc.invalidateQueries({ queryKey: queryKeys.gearRequests.all });
      qc.invalidateQueries({
        queryKey: queryKeys.gearRequests.detail(variables.id),
      });
      qc.invalidateQueries({ queryKey: queryKeys.crossModule.all });
    },
  });
}

// ─── Scan / Pack (GB06) ────────────────────────────────────────────────────

export function useScanGearRequest() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({
      id,
      data,
    }: {
      id: number;
      data: ScanGearRequestPayload;
    }): Promise<GearRequestLine> => {
      const response = await apiClient.post<GearRequestLine>(
        `${GEAR_REQUESTS_API}/${id}/scan/`,
        data
      );
      return response.data;
    },
    onSuccess: (_, variables) => {
      qc.invalidateQueries({ queryKey: queryKeys.gearRequests.all });
      qc.invalidateQueries({
        queryKey: queryKeys.gearRequests.detail(variables.id),
      });
      qc.invalidateQueries({ queryKey: queryKeys.crossModule.all });
    },
  });
}

// ─── Deploy (GB06) ───────────────────────────────────────────────────────────

export function useDeployGearRequest() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({
      id,
      data,
    }: {
      id: number;
      data: DeployGearRequestPayload;
    }): Promise<GearRequest> => {
      const response = await apiClient.post<GearRequest>(
        `${GEAR_REQUESTS_API}/${id}/deploy/`,
        data
      );
      return response.data;
    },
    onSuccess: (_, variables) => {
      qc.invalidateQueries({ queryKey: queryKeys.gearRequests.all });
      qc.invalidateQueries({
        queryKey: queryKeys.gearRequests.detail(variables.id),
      });
      qc.invalidateQueries({ queryKey: queryKeys.crossModule.all });
      qc.invalidateQueries({ queryKey: queryKeys.deployments.all });
      qc.invalidateQueries({ queryKey: queryKeys.assets.all });
    },
  });
}

// ─── Confirm / Reject Supervisor Ad-hoc Request (GB05) ─────────────────────

export function useConfirmSupervisorRequest() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({
      id,
      data,
    }: {
      id: number;
      data: ConfirmSupervisorRequestPayload;
    }): Promise<GearRequest> => {
      const response = await apiClient.post<GearRequest>(
        `${GEAR_REQUESTS_API}/${id}/confirm-supervisor-request/`,
        data
      );
      return response.data;
    },
    onSuccess: (_, variables) => {
      qc.invalidateQueries({ queryKey: queryKeys.gearRequests.all });
      qc.invalidateQueries({
        queryKey: queryKeys.gearRequests.detail(variables.id),
      });
      qc.invalidateQueries({ queryKey: queryKeys.crossModule.all });
    },
  });
}
